import React, { useState, useEffect, useRef } from 'react';
import type { TopBarProps, TopBarAction, Notification as TopBarNotification } from './types';
import { NotificationBell } from './NotificationBell';
import { ChatBell } from './ChatBell';
import { UserMenu } from './UserMenu';
import { Menu, X, Bell, User, Lock, LogOut } from '../icons';
import Tooltip from './Tooltip';

/**
 * Unified TopBar component for all modules
 * 
 * Structure: [Logo] [Left Content] [Center Content] [Actions] [Notifications] [User Menu]
 * 
 * Layout:
 * - Left: Logo + module-specific left content (flex-shrink-0)
 * - Center: Module-specific center content (flex-1 justify-center) - wycentrowany
 * - Right: Actions + Notifications + User Menu (flex-shrink-0)
 */
export const TopBar: React.FC<TopBarProps> = (props: TopBarProps) => {
  const {
    logo,
    leftContent,
    centerContent,
    actions = [] as TopBarAction[],
    notifications = [] as TopBarNotification[],
    onMarkAsRead,
    onMarkAllAsRead,
    onNotificationClick,
    chatConversations = [],
    chatUnreadCount = 0,
    onChatConversationClick,
    onOpenChat,
    user,
    onLogout,
    onChangePassword,
  } = props;
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileNotificationsOpen, setMobileNotificationsOpen] = useState(false);
  const [mobileUserMenuOpen, setMobileUserMenuOpen] = useState(false);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  const showNotifications = notifications !== undefined && onMarkAsRead && onMarkAllAsRead && onNotificationClick;
  const showChat = chatConversations !== undefined && onChatConversationClick && onOpenChat;
  const showUserMenu = user !== undefined && onLogout && onChangePassword;
  const showRightSection = actions.length > 0 || showNotifications || showChat || showUserMenu;

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target as Node)) {
        setMobileMenuOpen(false);
      }
    };

    if (mobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [mobileMenuOpen]);

  // Close mobile menu on escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [mobileMenuOpen]);

  return (
    <>
      <nav className="sticky top-0 left-0 right-0 z-50 bg-card shadow-sm" style={{ position: 'sticky', top: 0, left: 0, right: 0, backgroundColor: 'var(--card, #ffffff)', opacity: 1 }}>
        <style>
          {`
            /* Mobile menu animation */
            @keyframes slideIn {
              from {
                transform: translateX(-100%);
                opacity: 0;
              }
              to {
                transform: translateX(0);
                opacity: 1;
              }
            }

            .mobile-menu-enter {
              animation: slideIn 0.2s ease-out;
            }

            /* Hamburger menu - visible only on mobile */
            .hamburger-menu-button {
              display: flex;
            }

            @media (min-width: 768px) {
              .hamburger-menu-button {
                display: none !important;
              }
            }
          `}
        </style>
        
        <div className="flex items-center px-2 py-1">
          {/* Hamburger Menu Button - visible only on mobile */}
          {(actions.length > 0 || (notifications !== undefined && onMarkAsRead && onMarkAllAsRead && onNotificationClick) || (user !== undefined && onLogout && onChangePassword)) && (
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="hamburger-menu-button flex items-center justify-center text-text hover:text-accent transition-colors p-2 mr-2 cursor-pointer"
              aria-label="Otwórz menu"
              aria-expanded={mobileMenuOpen}
            >
              <Menu className="w-5 h-5" />
            </button>
          )}

          {/* Sekcja: Logo */}
          {logo && (
            <div className="topbar-sekcja-company-ikon flex items-center gap-2 flex-shrink-0" style={{ marginLeft: '20px' }}>
              {logo}
            </div>
          )}

          {/* Sekcja: Left Content (Region Picker, etc.) */}
          {leftContent && (
            <div className="topbar-sekcja-obiektow-modulu flex items-center gap-3 flex-shrink-0" style={{ marginLeft: '20px' }}>
              {leftContent}
            </div>
          )}

          {/* Sekcja: Center Content (Date Picker, etc.) - wycentrowany */}
          {centerContent && (
            <div className="flex items-center gap-2 flex-1 justify-center">
              {centerContent}
            </div>
          )}

          {/* Sekcja: Actions + Notifications + User */}
          {showRightSection && (
            <div className="flex items-center gap-3 ml-auto flex-shrink-0 pr-2 topbar-right">
              {actions.length > 0 && (
                <div className="topbar-sekcja-ikon-zadan flex items-center gap-2">
                  {actions.map((action) => (
                    <Tooltip key={action.id} content={action.label} position="bottom">
                      <button
                        onClick={action.onClick}
                        className="flex items-center justify-center text-text hover:text-accent transition-colors p-1 cursor-pointer"
                        aria-label={action.label}
                      >
                        {action.icon}
                      </button>
                    </Tooltip>
                  ))}
                </div>
              )}

              {actions.length > 0 && (showNotifications || showChat || showUserMenu) && (
                <div className="hidden md:block w-px h-6 bg-border" aria-hidden="true"></div>
              )}

              {(showNotifications || showChat || showUserMenu) && (
                <div className="topbar-sekcja-user-and-notification flex items-center gap-2">
                  {showNotifications && (
                    <NotificationBell
                      notifications={notifications}
                      onMarkAsRead={onMarkAsRead!}
                      onMarkAllAsRead={onMarkAllAsRead!}
                      onNotificationClick={onNotificationClick!}
                    />
                  )}
                  {showChat && (
                    <ChatBell
                      conversations={chatConversations}
                      unreadCount={chatUnreadCount}
                      onConversationClick={onChatConversationClick!}
                      onOpenChat={onOpenChat!}
                    />
                  )}
                  {showUserMenu && (
                    <UserMenu
                      user={user || null}
                      onLogout={onLogout!}
                      onChangePassword={onChangePassword!}
                    />
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </nav>

      {/* Mobile Menu Drawer */}
      {(actions.length > 0 || (notifications !== undefined && onMarkAsRead && onMarkAllAsRead && onNotificationClick) || (user !== undefined && onLogout && onChangePassword)) && mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-50" aria-hidden={!mobileMenuOpen}>
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/60" 
            onClick={() => setMobileMenuOpen(false)}
            aria-hidden="true"
          />
          
          {/* Menu Panel */}
          <div 
            ref={mobileMenuRef}
            className="absolute inset-y-0 left-0 w-72 max-w-[85vw] bg-white border-r border-border shadow-xl mobile-menu-enter overflow-y-auto"
            style={{ backgroundColor: '#ffffff' }}
          >
            {/* Menu Header */}
            <div className="h-14 flex items-center justify-between px-4 border-b border-border bg-white sticky top-0 z-10" style={{ backgroundColor: '#ffffff' }}>
              <h3 className="text-sm font-semibold text-text">Menu</h3>
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center justify-center text-text hover:text-accent transition-colors p-1 cursor-pointer"
                aria-label="Zamknij menu"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Menu Items */}
            <div className="p-2">
              {/* Actions */}
              {actions.map((action) => (
                <button
                  key={action.id}
                  onClick={() => {
                    action.onClick();
                    setMobileMenuOpen(false);
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm font-medium text-text hover:bg-gray-100 rounded-md transition-colors cursor-pointer"
                >
                  <span className="flex-shrink-0">{action.icon}</span>
                  <span>{action.label}</span>
                </button>
              ))}

              {/* Divider between actions and notifications/user */}
              {actions.length > 0 && ((notifications !== undefined && onMarkAsRead && onMarkAllAsRead && onNotificationClick) || (user !== undefined && onLogout && onChangePassword)) && (
                <div className="w-full h-px bg-border my-2" />
              )}

              {/* Notifications */}
              {notifications !== undefined && onMarkAsRead && onMarkAllAsRead && onNotificationClick && (
                <div className="relative">
                  <button
                    onClick={() => setMobileNotificationsOpen(!mobileNotificationsOpen)}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm font-medium text-text hover:bg-gray-100 rounded-md transition-colors cursor-pointer"
                  >
                    <span className="flex-shrink-0 relative">
                      <Bell className="w-5 h-5" />
                      {notifications.filter(n => !n.read).length > 0 && (
                        <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-red-500 rounded-full border-2 border-white">
                          {notifications.filter(n => !n.read).length > 99 ? '99+' : notifications.filter(n => !n.read).length}
                        </span>
                      )}
                    </span>
                    <span>Powiadomienia</span>
                    {notifications.filter(n => !n.read).length > 0 && (
                      <span className="ml-auto text-xs text-gray-500">
                        {notifications.filter(n => !n.read).length}
                      </span>
                    )}
                  </button>
                  
                  {/* Notifications Dropdown */}
                  {mobileNotificationsOpen && (
                    <div className="mt-1 mx-2 mb-2 bg-gray-50 border border-gray-200 rounded-lg max-h-[300px] overflow-y-auto">
                      {notifications.length === 0 ? (
                        <div className="px-4 py-8 text-center text-sm text-gray-500">
                          <Bell className="w-8 h-8 mx-auto mb-2 text-gray-400 opacity-50" />
                          <p>Brak powiadomień</p>
                        </div>
                      ) : (
                        <div className="divide-y divide-gray-200">
                          {notifications.map((notification) => (
                            <div
                              key={notification.id}
                              onClick={() => {
                                if (!notification.read) {
                                  onMarkAsRead(notification.id);
                                }
                                onNotificationClick(notification);
                                setMobileNotificationsOpen(false);
                              }}
                              className={`px-4 py-3 cursor-pointer hover:bg-white transition-colors ${
                                !notification.read ? 'bg-blue-50' : ''
                              }`}
                            >
                              <div className="flex items-start gap-2">
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-semibold text-text">{notification.title}</p>
                                  <p className="text-xs text-gray-600 mt-1 line-clamp-2">{notification.message}</p>
                                  <p className="text-xs text-gray-400 mt-1">
                                    {new Date(notification.createdAt).toLocaleDateString('pl-PL', { 
                                      day: '2-digit', 
                                      month: '2-digit',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })}
                                  </p>
                                </div>
                                {!notification.read && (
                                  <span className="flex-shrink-0 w-2 h-2 bg-blue-500 rounded-full mt-1.5"></span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      {notifications.filter(n => !n.read).length > 0 && (
                        <div className="px-4 py-2 border-t border-gray-200">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onMarkAllAsRead();
                            }}
                            className="w-full text-xs text-blue-600 hover:text-blue-700 font-medium text-center"
                          >
                            Oznacz wszystkie jako przeczytane
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* User Menu */}
              {user !== undefined && onLogout && onChangePassword && (
                <div className="relative">
                  <button
                    onClick={() => setMobileUserMenuOpen(!mobileUserMenuOpen)}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm font-medium text-text hover:bg-gray-100 rounded-md transition-colors cursor-pointer"
                  >
                    <span className="flex-shrink-0">
                      <User className="w-5 h-5" />
                    </span>
                    <span>
                      {user ? `${user.imie || ''} ${user.nazwisko || ''}`.trim() || 'Użytkownik' : 'Użytkownik'}
                    </span>
                  </button>
                  
                  {/* User Menu Dropdown */}
                  {mobileUserMenuOpen && (
                    <div className="mt-1 mx-2 mb-2 bg-gray-50 border border-gray-200 rounded-lg">
                      {user && (
                        <div className="px-4 py-3 border-b border-gray-200">
                          <div className="flex items-start gap-3">
                            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-br from-purple-400 to-purple-600 text-white font-semibold text-sm flex-shrink-0">
                              {user.imie?.charAt(0) || ''}{user.nazwisko?.charAt(0) || ''}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-text break-words">
                                {`${user.imie || ''} ${user.nazwisko || ''}`.trim() || 'Użytkownik'}
                              </p>
                              {user.email && (
                                <p className="text-xs text-gray-600 break-words mt-1">{user.email}</p>
                              )}
                              {user.role_names && user.role_names.length > 0 && (
                                <p className="text-xs text-gray-500 break-words mt-1">{user.role_names.join(', ')}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                      <div className="py-1">
                        <button
                          onClick={() => {
                            setMobileUserMenuOpen(false);
                            setMobileMenuOpen(false);
                            onChangePassword();
                          }}
                          className="w-full px-4 py-2 text-left text-sm text-text hover:bg-white flex items-center gap-3 transition-colors cursor-pointer"
                        >
                          <Lock className="w-4 h-4" />
                          <span>Zmień hasło</span>
                        </button>
                        <button
                          onClick={async () => {
                            setMobileUserMenuOpen(false);
                            setMobileMenuOpen(false);
                            await onLogout();
                          }}
                          className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-3 transition-colors cursor-pointer"
                        >
                          <LogOut className="w-4 h-4" />
                          <span>Wyloguj się</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TopBar;
